import { registerLocaleData } from '@angular/common'
import localeEn from '@angular/common/locales/en'
import localeJa from '@angular/common/locales/ja'
import localeKo from '@angular/common/locales/ko'
import localeZhHant from '@angular/common/locales/zh-Hant'
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { Router, RouteReuseStrategy } from '@angular/router'
import { IonicModule, IonicRouteStrategy } from '@ionic/angular'
import * as Sentry from '@sentry/angular'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { CommonComponentsModule } from './common/components/common-components.module'
import { CommonModalsModule } from './common/modals/common-modals.module'
import { KakaoCallbackComponent } from './kakao-callback.component'
import { LeadToAppModalComponent } from './lead-to-app-modal.component'
import { navAnimation } from './nav-transition.animation'
import { AlarmComponent } from './pages/alarm/alarm.component'
import { BubbleChartComponent } from './pages/bubble-chart/bubble-chart.component'
import { BubbleContentComponent } from './pages/bubble-chart/components/bubble-content/bubble-content.component'
import { InstallComponent } from './pages/install/install.component'
import { AskMainComponent } from './pages/main/ask-main/ask-main.component'
import { MainHeaderComponent } from './pages/main/main-header/main-header.component'
import { MainComponent } from './pages/main/main.component'
import { SurveyMainComponent } from './pages/main/survey-main/survey-main.component'
import { TermsComponent } from './pages/terms/terms.component'
import { TypesStoryComponent } from './pages/types-story/types-story.component'
import { RequestAdapter, ResponseAdapter } from './services/http/http.service'
import { SharedModule } from './shared/shared.module'
// import ngx-translate and the http loader
import { HttpClient, HttpClientJsonpModule, HttpClientModule } from '@angular/common/http'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { Observable } from 'rxjs'

import kebabCase from 'lodash.kebabcase'

const isLocaleEn = location.hostname.includes('en.')
const isLocaleZhHant = location.hostname.includes('tw.')
const isLocaleJa = location.hostname.includes('jp.')

const locale = isLocaleEn ? localeEn : isLocaleZhHant ? localeZhHant : isLocaleJa ? localeJa : localeKo
const localeString = isLocaleEn ? 'en-US' : isLocaleZhHant ? 'zh-TW' : isLocaleJa ? 'ja-JP' : 'ko-KR'
registerLocaleData(locale)

@NgModule({
  declarations: [
    AppComponent,
    MainComponent,
    KakaoCallbackComponent,
    TermsComponent,
    AlarmComponent,
    LeadToAppModalComponent,
    AskMainComponent,
    SurveyMainComponent,
    MainHeaderComponent,
    BubbleChartComponent,
    BubbleContentComponent,
    InstallComponent,
    TypesStoryComponent,
  ],
  entryComponents: [],
  imports: [
    FormsModule,
    IonicModule.forRoot({ navAnimation }),
    SharedModule,
    BrowserAnimationsModule,
    HttpClientJsonpModule,
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    CommonComponentsModule,
    CommonModalsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    ResponseAdapter,
    RequestAdapter,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    { provide: LOCALE_ID, useValue: localeString },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient) {
  return new SingleFileLoader(http, './assets/i18n/merged', '.json')
}

export class SingleFileLoader implements TranslateLoader {
  constructor(private http: HttpClient, private prefix: string, private suffix: string) {}

  public getTranslation(lang: string): Observable<any> {
    /* 
    lang(camelCase) -> kabab-case
    ex)zhHant -> zh-hant
     */
    const formattedLang = kebabCase(lang)
    const path = `${this.prefix}/${formattedLang}${this.suffix}`
    return this.http.get(path)
  }
}
